import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { TSP_MAP_CONFIG } from "../../constants/tspMapConfig";
import {
  MARKER_STATE,
  getVisitMarkerState,
  getJourneyMapPopupData,
  clusterPoints,
  outsideIndiaMask,
} from "../../pages/journeys/coverageMapModel";

// A real, interactive geographic map (MapLibre GL + OpenStreetMap vector tiles,
// provider in constants/tspMapConfig.js) that plots visit points exactly at
// their saved latitude/longitude, over clickable Survey of India state outlines.
//
//   points            API points ({ key, latitude, longitude, marker, state_id, label, ... })
//   selectedKey       key of the highlighted point
//   onPointClick      (point) => void
//   onClusterClick    ({ stateId, points }) => void — nearby points of ONE state
//   boundaries        state GeoJSON (public/tsp/geo) or null — outline + click target only
//   nationalBoundary  India's national boundary GeoJSON (public/tsp/geo) or null
//   visitedLgdCodes   polygons tinted as "has visits"
//   selectedLgdCodes  polygons highlighted as the selected state
//   onStateClick      (feature) => void
//   focus             { key, bounds: [[s, w], [n, e]], maxZoom } | { key, center: [lat, lng], zoom } | { key }
//                     — a NEW key moves the map (a bare key = back to India)
//   onMapPick         ({ lat, lng }) => void — the exact clicked coordinate. Given,
//                     every click on the map itself (not on a marker, popup or
//                     control) picks that point, and Enter on the focused map picks
//                     its centre; a state outline is then not a click target (states
//                     open from the caller's list and the clusters). Omitted, a click
//                     on a state opens it.
//   pickedPoint       { latitude, longitude } of the picked point (a distinct pin) or null
//   pickedContent     React node shown for the picked point: in a map popup on wide
//                     screens, as a bottom sheet on phones
//   searchedPlace     { latitude, longitude, label } of a place found with the location
//                     search, or null: a red pin with the place's name, so the person
//                     sees where it is. Display only — it takes no clicks (a click
//                     there still reaches the map) and leaves every other marker alone.
//   describeCluster   (cluster) => { count, lines, label } — a cluster's number, hover
//                     lines and accessible label; omitted, it counts visited places
//
// Markers are HTML elements (styles in _coverage-map.scss): "visited" dots, a
// distinct "upcoming" marker, a pulsing "current" marker (static under
// prefers-reduced-motion, and always distinct by size and ring), and numbered clusters.
// Every marker is keyboard-focusable, announces what it is, and shows its details
// on hover or focus, and opens an interactive popup with journey details on click.
const STATES = "tsp-states";
// Phones get the picked point's panel as a bottom sheet instead of a popup.
const SHEET_QUERY = "(max-width: 576px)";

function useMatchMedia(query) {
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    if (list.addEventListener) list.addEventListener("change", onChange);
    else if (list.addListener) list.addListener(onChange);
    return () => {
      if (list.removeEventListener) list.removeEventListener("change", onChange);
      else if (list.removeListener) list.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

// A click that landed on a marker or a popup is theirs, not a pick on the map.
const clickedOnOverlay = (event) => {
  const target = event && event.originalEvent && event.originalEvent.target;
  return Boolean(target && typeof target.closest === "function" && target.closest(".maplibregl-marker, .maplibregl-popup, .tsp-cov-marker"));
};

function textBox(lines) {
  const box = document.createElement("div");
  box.className = "tsp-map-tip";
  lines.filter(Boolean).forEach((line, index) => {
    const row = document.createElement(index === 0 ? "strong" : "div");
    row.textContent = line;
    box.appendChild(row);
  });
  return box;
}

function pointLines(point) {
  const state = getVisitMarkerState(point);
  const statusLabel =
    point.marker === "not_visited"
      ? "Not visited (the journey has ended)"
      : state === MARKER_STATE.CURRENT
      ? "Current city of a journey under way"
      : state === MARKER_STATE.UPCOMING
      ? "Upcoming planned visit"
      : point.historical
      ? "Visited on a past (completed / cancelled) journey"
      : "Visited";
  // A journey place: its own name ("<Town> - <Temple>") and date; a shared spot says how many places.
  if (point.place_id) {
    const shared = (point.places || []).length;
    return [
      shared > 1 ? `${point.city_name || point.label} — ${shared} journey places` : point.place_label || point.label || point.city_name,
      statusLabel,
      shared > 1 ? "Select to see each place" : point.place_date ? `Date ${point.place_date}` : "No date yet",
      `${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}`,
    ];
  }
  return [
    point.label || point.city_name,
    statusLabel,
    `${point.visit_count || 0} visit${point.visit_count === 1 ? "" : "s"}${point.last_visit_date ? ` · last ${point.last_visit_date}` : ""}`,
    `${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}`,
  ];
}

function createMapPopupContent(popupData, onNavigate) {
  const container = document.createElement("div");
  container.className = "tsp-popup-card";

  // Header
  const header = document.createElement("div");
  header.className = "tsp-popup-header";

  const title = document.createElement("h4");
  title.className = "tsp-popup-title";
  title.textContent = popupData.talukaName;
  header.appendChild(title);

  const badge = document.createElement("span");
  badge.className = `tsp-popup-badge tsp-popup-badge--${popupData.markerState}${popupData.historical && popupData.markerState === "past" ? " tsp-popup-badge--historical" : ""}`;
  badge.textContent = popupData.notVisited
    ? "Not visited"
    : popupData.markerState === "current"
      ? "Current"
      : popupData.markerState === "upcoming"
      ? "Upcoming"
      : popupData.historical
      ? "Past journey"
      : "Visited";
  header.appendChild(badge);

  container.appendChild(header);

  // Facts
  const dl = document.createElement("dl");
  dl.className = "tsp-popup-facts";

  const addRow = (label, value) => {
    if (!value) return;
    const dt = document.createElement("dt");
    dt.textContent = label;
    const dd = document.createElement("dd");
    dd.textContent = value;
    dl.appendChild(dt);
    dl.appendChild(dd);
  };

  addRow("Journey", popupData.journeyName);
  if (popupData.year) addRow("Year", String(popupData.year));
  addRow("Temple", popupData.templeName);
  addRow("District", popupData.districtName);
  addRow("State", popupData.stateName);
  if (popupData.journeyStatus) {
    addRow("Journey Status", popupData.journeyStatus.replace(/_/g, " "));
  }
  if (popupData.visitDate) {
    addRow(popupData.dateLabel || "Visit Date", popupData.visitDate);
  }
  if (popupData.leaderName) {
    addRow("Leader", popupData.leaderName);
  }
  if (popupData.areasVisited) {
    // An upcoming stop has not been visited: its areas are the planned ones.
    addRow(popupData.markerState === "upcoming" ? "Planned Areas" : "Areas Visited", popupData.areasVisited);
  }
  addRow("Route", popupData.routeText);
  addRow("Progress", popupData.routeProgress);
  if (popupData.coordinates) {
    addRow("Coordinates", popupData.coordinates);
  }

  container.appendChild(dl);

  // A spot visited by several journeys (often the same yatra in different
  // years): each one, newest year first, opens its own journey.
  const goTo = (path) => (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate(path);
    else if (typeof window !== "undefined") window.location.href = path;
  };
  const go = (journeyId) => goTo(`/tsp/journeys/${journeyId}`);
  // Journey places sharing this spot: each one opens its own journey place.
  if (popupData.placesHere && popupData.placesHere.length > 1) {
    const section = document.createElement("div");
    section.className = "tsp-popup-journeys";
    const heading = document.createElement("span");
    heading.className = "tsp-popup-journeys-title";
    heading.textContent = `Journey places here (${popupData.placesHere.length})`;
    section.appendChild(heading);
    const list = document.createElement("ul");
    popupData.placesHere.forEach((entry) => {
      const item = document.createElement("li");
      const link = document.createElement("button");
      link.type = "button";
      link.className = `tsp-popup-journey-link${entry.placeId === popupData.placeId ? " is-current" : ""}`;
      link.textContent = `${entry.label}${entry.date ? ` · ${entry.date}` : ""}`;
      link.addEventListener("click", goTo(entry.path));
      item.appendChild(link);
      list.appendChild(item);
    });
    section.appendChild(list);
    container.appendChild(section);
  }
  if (popupData.journeysHere && popupData.journeysHere.length > 1) {
    const section = document.createElement("div");
    section.className = "tsp-popup-journeys";
    const heading = document.createElement("span");
    heading.className = "tsp-popup-journeys-title";
    heading.textContent = `Journeys here (${popupData.journeysHere.length})`;
    section.appendChild(heading);
    const list = document.createElement("ul");
    popupData.journeysHere.forEach((entry) => {
      const item = document.createElement("li");
      const link = document.createElement("button");
      link.type = "button";
      link.className = `tsp-popup-journey-link${entry.id === popupData.journeyId ? " is-current" : ""}`;
      link.textContent = `${entry.year || "No date"} · ${entry.name}`;
      link.addEventListener("click", go(entry.id));
      item.appendChild(link);
      list.appendChild(item);
    });
    section.appendChild(list);
    container.appendChild(section);
  }

  // Action Footer
  if (popupData.journeyId) {
    const footer = document.createElement("div");
    footer.className = "tsp-popup-footer";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tsp-popup-btn";
    btn.textContent = "View Journey";
    btn.addEventListener("click", popupData.path ? goTo(popupData.path) : go(popupData.journeyId));
    footer.appendChild(btn);
    container.appendChild(footer);
  }

  return container;
}

function markerElement({ className, html, label, onActivate }) {
  const el = document.createElement("div");
  el.className = className;
  el.innerHTML = html;
  el.tabIndex = 0;
  el.setAttribute("role", "button");
  el.setAttribute("aria-label", label);
  el.addEventListener("click", (event) => {
    event.stopPropagation();
    onActivate();
  });
  el.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  });
  return el;
}

function hideStyleLayers(map) {
  TSP_MAP_CONFIG.hiddenStyleLayers.forEach((id) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", "none");
  });
}

// Leaves India's own name out of the base style's country labels (by its
// country code, or its name where a label has no code); other countries' names
// and every place name stay as they are.
function hideCountryLabel(map) {
  const { layers, iso, name } = TSP_MAP_CONFIG.hiddenCountryLabel;
  const notThisCountry = ["!", ["any", ["==", ["get", "iso_a2"], iso], ["==", ["get", "name:latin"], name], ["==", ["get", "name_en"], name]]];
  layers.forEach((id) => {
    if (!map.getLayer(id)) return;
    const current = map.getFilter(id);
    map.setFilter(id, current ? ["all", current, notThisCountry] : notThisCountry);
  });
}

function addNationalBoundary(map, national) {
  if (!national || map.getSource("tsp-india")) return;
  map.addSource("tsp-india", { type: "geojson", data: national });
  const mask = outsideIndiaMask(national);
  if (mask) {
    map.addSource("tsp-outside-india", { type: "geojson", data: mask });
    map.addLayer({
      id: "tsp-outside-india-mask",
      type: "fill",
      source: "tsp-outside-india",
      paint: { "fill-color": TSP_MAP_CONFIG.outsideIndiaColor, "fill-opacity": 1 },
    });
    map.addLayer({
      id: "tsp-india-border",
      type: "line",
      source: "tsp-india",
      paint: {
        "line-color": "#6b5b7b",
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 1.1, 8, 2.2],
        "line-opacity": 0.85,
      },
    });
    return;
  }
  const firstSymbol = (map.getStyle().layers || []).find((layer) => layer.type === "symbol");
  map.addLayer(
    {
      id: "tsp-india-border",
      type: "line",
      source: "tsp-india",
      paint: {
        "line-color": "#6b5b7b",
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 1.1, 8, 2.2],
        "line-opacity": 0.85,
      },
    },
    firstSymbol ? firstSymbol.id : undefined
  );
}

function addStateLayers(map, boundaries) {
  if (!boundaries || map.getSource(STATES)) return;
  map.addSource(STATES, { type: "geojson", data: boundaries, promoteId: "lgd_code" });
  const firstSymbol = (map.getStyle().layers || []).find((layer) => layer.type === "symbol");
  const before = firstSymbol ? firstSymbol.id : undefined;
  map.addLayer(
    {
      id: "tsp-states-fill",
      type: "fill",
      source: STATES,
      paint: {
        "fill-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#f97316",
          ["boolean", ["feature-state", "visited"], false],
          "#e07b1a",
          "#1e3a8a",
        ],
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          0.16,
          ["boolean", ["feature-state", "hover"], false],
          0.1,
          ["boolean", ["feature-state", "visited"], false],
          0.06,
          0,
        ],
      },
    },
    before
  );
  map.addLayer(
    {
      id: "tsp-states-line",
      type: "line",
      source: STATES,
      paint: {
        "line-color": ["case", ["boolean", ["feature-state", "selected"], false], "#c2410c", "#6b7280"],
        "line-width": ["case", ["boolean", ["feature-state", "selected"], false], 2.4, 0.9],
        "line-dasharray": ["case", ["boolean", ["feature-state", "selected"], false], ["literal", [1, 0]], ["literal", [3, 2]]],
      },
    },
    before
  );
}

export default function TspMap({
  points = [],
  selectedKey = null,
  onPointClick,
  onClusterClick,
  boundaries = null,
  nationalBoundary = null,
  visitedLgdCodes = [],
  selectedLgdCodes = [],
  onStateClick,
  focus = null,
  ariaLabel = "Map",
  className = "",
  currentJourney = null,
  coverage = null,
  onMapPick,
  pickedPoint = null,
  pickedContent = null,
  searchedPlace = null,
  describeCluster,
  onResetView,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const tipRef = useRef(null);
  const popupRef = useRef(null);
  const pickedMarkerRef = useRef(null);
  const pickedPopupRef = useRef(null);
  const searchedMarkerRef = useRef(null);
  const [pickedPopupNode] = useState(() => (typeof document !== "undefined" ? document.createElement("div") : null));
  const asSheet = useMatchMedia(SHEET_QUERY);
  const [isZoomed, setIsZoomed] = useState(false);
  const initialZoomRef = useRef(null);
  
  let navigate = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate();
  } catch {
    navigate = (path) => {
      if (typeof window !== "undefined") window.location.href = path;
    };
  }

  const handlers = useRef({});
  handlers.current = { onPointClick, onClusterClick, onStateClick, boundaries, currentJourney, coverage, onNavigate: navigate, onMapPick, describeCluster, onResetView };
  const [ready, setReady] = useState(false);
  const [view, setView] = useState(0);
  const [baseMapFailed, setBaseMapFailed] = useState(false);

  // Create the map once.
  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: TSP_MAP_CONFIG.styleUrl,
      center: TSP_MAP_CONFIG.india.center,
      zoom: TSP_MAP_CONFIG.india.zoom,
      minZoom: 2.5,
      maxZoom: 17,
      cooperativeGestures: true,
      renderWorldCopies: false,
      attributionControl: { compact: true, customAttribution: TSP_MAP_CONFIG.statesAttribution },
    });
    const collapseAttribution = () => {
      const attrib = containerRef.current?.querySelector?.(".maplibregl-ctrl-attrib");
      if (attrib) {
        attrib.classList.remove("maplibregl-compact-show");
        attrib.classList.add("maplibregl-compact");
        attrib.removeAttribute("open");
      }
    };

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    map.fitBounds(TSP_MAP_CONFIG.india.bounds, { padding: 12, duration: 0 });
    setTimeout(collapseAttribution, 0);

    const checkZoom = () => {
      if (!map || typeof map.getZoom !== "function") return;
      const currentZoom = map.getZoom();
      const initZoom = initialZoomRef.current ?? 3.8;
      setIsZoomed(currentZoom > initZoom + 0.35);
    };

    map.on("load", () => {
      hideStyleLayers(map);
      hideCountryLabel(map);
      collapseAttribution();
      if (typeof map.resize === "function") map.resize();
      map.fitBounds(TSP_MAP_CONFIG.india.bounds, { padding: 8, duration: 0 });
      if (typeof map.getZoom === "function") {
        initialZoomRef.current = map.getZoom();
      }
      setReady(true);
    });
    map.on("styledata", collapseAttribution);
    map.on("error", (event) => {
      if (!map.isStyleLoaded() && event && event.error && /style/i.test(String(event.error.message || ""))) {
        setBaseMapFailed(true);
      }
    });
    map.on("zoomend", checkZoom);
    map.on("moveend", () => {
      setView((n) => n + 1);
      checkZoom();
    });

    let hovered = null;
    const setHover = (id) => {
      if (hovered !== null && map.getSource(STATES)) map.setFeatureState({ source: STATES, id: hovered }, { hover: false });
      hovered = id;
      if (id !== null && map.getSource(STATES)) map.setFeatureState({ source: STATES, id }, { hover: true });
    };
    // A state outline invites a click only where a click opens the state.
    map.on("mousemove", "tsp-states-fill", (event) => {
      if (handlers.current.onMapPick) return;
      map.getCanvas().style.cursor = "pointer";
      setHover(event.features && event.features[0] ? event.features[0].id : null);
    });
    map.on("mouseleave", "tsp-states-fill", () => {
      if (handlers.current.onMapPick) return;
      map.getCanvas().style.cursor = "";
      setHover(null);
    });
    map.on("click", "tsp-states-fill", (event) => {
      if (handlers.current.onMapPick) return; // the click picks a point, it does not open a state
      const feature = event.features && event.features[0];
      if (!feature || !handlers.current.onStateClick) return;
      const source = (handlers.current.boundaries?.features || []).find((f) => f.properties.lgd_code === feature.id);
      handlers.current.onStateClick(source || feature);
    });

    // A click on the map picks the exact clicked coordinate. Zoomed far out, a
    // click is only roughly placed, so the map also zooms in on it to let the
    // person refine it.
    map.on("click", (event) => {
      if (!handlers.current.onMapPick || !event || !event.lngLat || clickedOnOverlay(event)) return;
      handlers.current.onMapPick({ lat: event.lngLat.lat, lng: event.lngLat.lng });
      if (typeof map.getZoom === "function" && map.getZoom() < TSP_MAP_CONFIG.pickRefineZoom) {
        map.flyTo({ center: [event.lngLat.lng, event.lngLat.lat], zoom: TSP_MAP_CONFIG.pickRefineZoom, duration: 800 });
      }
    });

    // Keyboard: the map pans with the arrow keys; Enter picks its centre.
    const canvas = typeof map.getCanvas === "function" ? map.getCanvas() : null;
    const pickCentre = (event) => {
      if (event.key !== "Enter" || !handlers.current.onMapPick || typeof map.getCenter !== "function") return;
      // The popup moves focus into the panel (onto its Close button); without this
      // the same key press would then activate that button and close the panel again.
      event.preventDefault();
      const center = map.getCenter();
      handlers.current.onMapPick({ lat: center.lat, lng: center.lng });
    };
    if (canvas && canvas.addEventListener) canvas.addEventListener("keydown", pickCentre);

    mapRef.current = map;
    return () => {
      if (canvas && canvas.removeEventListener) canvas.removeEventListener("keydown", pickCentre);
      if (pickedPopupRef.current) pickedPopupRef.current.remove();
      if (pickedMarkerRef.current) pickedMarkerRef.current.remove();
      if (popupRef.current) popupRef.current.remove();
      if (tipRef.current) tipRef.current.remove();
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Resize map whenever container dimensions change
  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      map.resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [ready]);

  // Boundary layers
  useEffect(() => {
    const map = mapRef.current;
    if (map && ready && boundaries) addStateLayers(map, boundaries);
  }, [ready, boundaries]);
  useEffect(() => {
    const map = mapRef.current;
    if (map && ready && nationalBoundary) addNationalBoundary(map, nationalBoundary);
  }, [ready, nationalBoundary]);

  // Tinted / selected polygons
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !boundaries || !map.getSource(STATES)) return;
    boundaries.features.forEach((feature) => {
      const id = feature.properties.lgd_code;
      map.setFeatureState({ source: STATES, id }, { visited: visitedLgdCodes.includes(id), selected: selectedLgdCodes.includes(id) });
    });
  }, [ready, boundaries, visitedLgdCodes, selectedLgdCodes]);

  // Draw markers for current view
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const showTip = (lngLat, lines) => {
      if (popupRef.current && typeof popupRef.current.isOpen === "function" && popupRef.current.isOpen()) return;
      if (tipRef.current) tipRef.current.remove();
      tipRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
        className: "tsp-map-tip-popup",
      })
        .setLngLat(lngLat)
        .setDOMContent(textBox(lines))
        .addTo(map);
    };

    const hideTip = () => {
      if (tipRef.current) tipRef.current.remove();
      tipRef.current = null;
    };

    const openPointPopup = (lngLat, point) => {
      hideTip();
      if (popupRef.current) popupRef.current.remove();
      const popupData = getJourneyMapPopupData(point, handlers.current.currentJourney, handlers.current.coverage);
      const content = createMapPopupContent(popupData, handlers.current.onNavigate);
      popupRef.current = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: true,
        offset: 16,
        className: "tsp-map-popup",
        maxWidth: "320px",
      })
        .setLngLat(lngLat)
        .setDOMContent(content)
        .addTo(map);
    };

    const project = (lat, lng) => map.project([lng, lat]);

    clusterPoints(points, project).forEach((item) => {
      let el;
      let lngLat;
      let lines;
      if (item.type === "cluster") {
        lngLat = [item.longitude, item.latitude];
        const described = handlers.current.describeCluster ? handlers.current.describeCluster(item) : null;
        lines = described ? described.lines : [`${item.points.length} visited places`, "Select to open this state"];
        el = markerElement({
          className: "tsp-cov-marker tsp-cov-marker--cluster",
          html: `<span class="tsp-cov-cluster-count">${Number(described ? described.count : item.points.length)}</span>`,
          label: described ? described.label : `${item.points.length} visited places close together — open this state`,
          onActivate: () => handlers.current.onClusterClick && handlers.current.onClusterClick(item),
        });
      } else {
        const { point } = item;
        const markerState = getVisitMarkerState(point);
        const isCurrent = markerState === MARKER_STATE.CURRENT;
        const isUpcoming = markerState === MARKER_STATE.UPCOMING;

        lngLat = [point.longitude, point.latitude];
        lines = pointLines(point);

        let markerClass = point.historical ? "tsp-cov-marker--visited tsp-cov-marker--historical" : "tsp-cov-marker--visited";
        let markerHtml = '<span class="tsp-cov-marker-core"></span>';

        if (isCurrent) {
          markerClass = "tsp-cov-marker--current";
          markerHtml = '<span class="tsp-cov-marker-pulse"></span><span class="tsp-cov-marker-core"></span>';
        } else if (isUpcoming) {
          // An ended journey's unvisited place: hollow like a planned stop, greyed as history.
          markerClass = point.marker === "not_visited" ? "tsp-cov-marker--upcoming tsp-cov-marker--not-visited" : "tsp-cov-marker--upcoming";
          markerHtml = '<span class="tsp-cov-marker-upcoming-core"></span>';
        }

        el = markerElement({
          className: `tsp-cov-marker ${markerClass}${point.key === selectedKey ? " is-selected" : ""}`,
          html: markerHtml,
          label: `${lines[0]}: ${lines[1]}`,
          onActivate: () => {
            openPointPopup(lngLat, point);
            if (handlers.current.onPointClick) handlers.current.onPointClick(point);
          },
        });

        if (isCurrent) el.style.zIndex = "3";
        else if (isUpcoming) el.style.zIndex = "2";
        else if (point.key === selectedKey) el.style.zIndex = "2";
      }

      el.dataset.lat = String(lngLat[1]);
      el.dataset.lng = String(lngLat[0]);
      el.addEventListener("mouseenter", () => showTip(lngLat, lines));
      el.addEventListener("focus", () => showTip(lngLat, lines));
      el.addEventListener("mouseleave", hideTip);
      el.addEventListener("blur", hideTip);
      markersRef.current.push(new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(lngLat).addTo(map));
    });

    return () => {
      hideTip();
    };
  }, [points, selectedKey, view]);

  // Where a click picks a point: a crosshair, and the keyboard way told to screen readers.
  const canPick = Boolean(onMapPick);
  useEffect(() => {
    const map = mapRef.current;
    const canvas = map && typeof map.getCanvas === "function" ? map.getCanvas() : null;
    if (!canvas) return;
    canvas.style.cursor = canPick ? "crosshair" : "";
    if (!canvas.setAttribute) return;
    if (canPick) canvas.setAttribute("aria-description", "Click the map, or press Enter for its centre, to select a location");
    else canvas.removeAttribute("aria-description");
  }, [canPick]);

  // The picked point: its own pin (never confused with a visit marker), and its
  // panel in a popup anchored to it — unless phones show the panel as a sheet.
  const pickedLat = pickedPoint ? pickedPoint.latitude : null;
  const pickedLng = pickedPoint ? pickedPoint.longitude : null;
  useEffect(() => {
    const map = mapRef.current;
    if (pickedMarkerRef.current) pickedMarkerRef.current.remove();
    pickedMarkerRef.current = null;
    if (!map || pickedLat === null || pickedLng === null) return;
    const el = document.createElement("div");
    el.className = "tsp-cov-marker tsp-cov-marker--picked";
    el.innerHTML = '<span class="tsp-cov-marker-picked-pin"></span>';
    el.setAttribute("role", "img");
    el.setAttribute("aria-label", `Selected location ${pickedLat.toFixed(5)}, ${pickedLng.toFixed(5)}`);
    el.dataset.lat = String(pickedLat);
    el.dataset.lng = String(pickedLng);
    el.style.zIndex = "4";
    pickedMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([pickedLng, pickedLat]).addTo(map);
  }, [pickedLat, pickedLng]);

  // The searched place: a pin anchored at its tip on the exact coordinate, with
  // the place's name beside it. A new place replaces it; null removes it.
  const searchedLat = searchedPlace ? searchedPlace.latitude : null;
  const searchedLng = searchedPlace ? searchedPlace.longitude : null;
  const searchedLabel = searchedPlace ? searchedPlace.label || "" : "";
  useEffect(() => {
    const map = mapRef.current;
    if (searchedMarkerRef.current) searchedMarkerRef.current.remove();
    searchedMarkerRef.current = null;
    if (!map || !Number.isFinite(searchedLat) || !Number.isFinite(searchedLng)) return;
    const el = document.createElement("div");
    el.className = "tsp-cov-marker tsp-cov-marker--searched";
    el.setAttribute("role", "img");
    el.setAttribute("aria-label", searchedLabel ? `Searched location: ${searchedLabel}` : "Searched location");
    const pin = document.createElement("span");
    pin.className = "tsp-cov-marker-searched-pin";
    el.appendChild(pin);
    if (searchedLabel) {
      const name = document.createElement("span");
      name.className = "tsp-cov-marker-searched-label";
      name.textContent = searchedLabel; // text, never HTML
      el.appendChild(name);
    }
    el.dataset.lat = String(searchedLat);
    el.dataset.lng = String(searchedLng);
    el.style.zIndex = "5";
    searchedMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([searchedLng, searchedLat]).addTo(map);
  }, [searchedLat, searchedLng, searchedLabel]);

  const showPickedPopup = Boolean(pickedPoint && pickedContent && !asSheet && pickedPopupNode);
  useEffect(() => {
    const map = mapRef.current;
    if (pickedPopupRef.current) pickedPopupRef.current.remove();
    pickedPopupRef.current = null;
    if (!map || !showPickedPopup) return;
    if (popupRef.current) popupRef.current.remove(); // one popup at a time
    pickedPopupRef.current = new maplibregl.Popup({
      closeButton: false, // the panel has its own Close
      closeOnClick: false, // a click on the map picks a new point instead
      offset: 30,
      className: "tsp-map-popup tsp-map-popup--picked",
      maxWidth: "300px",
    })
      .setLngLat([pickedLng, pickedLat])
      .setDOMContent(pickedPopupNode)
      .addTo(map);
  }, [showPickedPopup, pickedLat, pickedLng, pickedPopupNode]);

  // Move map when asked to
  const focusKey = focus ? focus.key : null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    if (focus.bounds) {
      setIsZoomed(true);
      const [[south, west], [north, east]] = focus.bounds;
      map.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: focus.padding || 48, maxZoom: focus.maxZoom || TSP_MAP_CONFIG.cityZoom, duration: 800 }
      );
    } else if (focus.center) {
      setIsZoomed(true);
      map.flyTo({ center: [focus.center[1], focus.center[0]], zoom: focus.zoom || TSP_MAP_CONFIG.cityZoom, duration: 800 });
    } else {
      setIsZoomed(false);
      map.fitBounds(TSP_MAP_CONFIG.india.bounds, { padding: 12, duration: 800 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const handleResetToFullMap = () => {
    const map = mapRef.current;
    if (!map) return;
    map.fitBounds(TSP_MAP_CONFIG.india.bounds, { padding: 8, duration: 600 });
    setIsZoomed(false);
    if (handlers.current.onResetView) {
      handlers.current.onResetView();
    }
  };

  return (
    <div className={`tsp-map-wrap ${className}`}>
      <div ref={containerRef} className="tsp-map" role="region" aria-label={ariaLabel} data-testid="tsp-map" />
      {isZoomed && (
        <button
          type="button"
          className="tsp-map-back-btn"
          onClick={handleResetToFullMap}
          title="Back to full map"
          aria-label="Back to full map"
        >
          <RotateCcw size={13} aria-hidden="true" />
          <span>Back to Map</span>
        </button>
      )}
      {baseMapFailed && <div className="tsp-cov-map-empty">The base map could not be loaded. Visit markers are still shown at their saved coordinates.</div>}
      {showPickedPopup && createPortal(pickedContent, pickedPopupNode)}
      {pickedPoint && pickedContent && asSheet && <div className="tsp-map-sheet">{pickedContent}</div>}
    </div>
  );
}
